import axios from "axios";
import { toast } from "react-toastify";

import { AuthStorage } from "./AuthTokenService";
import { jwtDecode } from "jwt-decode";
import { userThunks } from "user/presentation/store/userSlice";
import { AsyncThunkAction, Dispatch } from "@reduxjs/toolkit";
import store, { RootState } from "../store";
import { useAppDispatch } from "../store/hooks";
import { useNavigate } from "react-router-dom";

type NavigateFunction = (path: string) => void;

interface NavigationState {
  navigate: NavigateFunction | null;
}

export const navigationState: NavigationState = {
  navigate: null,
};

export const initializeNavigationService = (navigate: NavigateFunction) => {
  navigationState.navigate = navigate;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

// Debounced version of handleUnauthorizedError
let isHandlingUnauthorized = false;
const handleUnauthorizedError = async () => {
  if (isHandlingUnauthorized) {
    return; // Prevent multiple calls
  }

  try {
    isHandlingUnauthorized = true;
    await AuthStorage.removeSession();
    await AuthStorage.removeToken();

    if (navigationState.navigate) {
      navigationState.navigate("/");
    } else {
      console.log("Navigation service not initialized");
      window.location.href = "/";
    }
  } finally {
    isHandlingUnauthorized = false;
  }
};

const getBaseUrl = async () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  return API_BASE_URL;
};

const service = axios.create({
  headers: {
    Accept: "application/json",
    // 'Content-Type': 'multipart/form-data'
  },
});

export const blobService = axios.create({
  headers: {
    Accept: "application/json",
  },
  responseType: "blob",
});

// Config
const TOKEN_PAYLOAD_KEY = "authorization";
// const PUBLIC_REQUEST_KEY = 'public-request'

const refreshAccessTokenAfterExpiration = async () => {
  const accessToken = localStorage.getItem("accessToken") || "";
  if (accessToken) {
    if (AuthStorage.isTokenExpired(accessToken)) {
      try {
        await refreshAccessToken();
      } catch (refresherror) {
        console.log("refresh token failed", refresherror);
      }
    }
  }
};

// API Request interceptor
service.interceptors.request.use(
  async (config) => {
    await refreshAccessTokenAfterExpiration();
    config.baseURL = await getBaseUrl();
    const jwtToken = await localStorage.getItem("accessToken");
    // const jwtToken = await AuthStorage.getToken();
    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = "Bearer " + jwtToken;
    }

    return config;
  },
  (error) => {
    toast.error("Error...");
    Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    if (error.response && error.response.status === 400) {
      // Check if the server provided a custom error message
      if (error.response.data && error.response.data.message) {
        toast.error("Error", error.response.data.message);
        return Promise.reject(error);
      }

      // If no custom error message is provided, you can handle it generically
      toast.error("Invalid data provided.");
      return Promise.reject(error);
    }

    // Handle other status codes or errors here
    if (error.response && error.response.status === 422) {
      return Promise.reject(error.response?.data?.errors);
    }

    if (error.response && error.response.status === 401) {
      handleUnauthorizedError();
    }

    if (error.response && error.response.status === 404) {
      toast.error("Not Found");
    }

    if (error.response && error.response.status === 500) {
      toast.error("Internal Server Error");
    }

    if (error.response && error.response.status === 508) {
      toast.error("Time Out");
    }

    toast.error("Error Occurred!!");

    return Promise.reject(error);
  }
);
export default service;
const refreshAccessToken = async () => {
  try {
    const response = await axios({
      url: (await getBaseUrl()) + "/auth/refresh",
      method: "get",
      headers: {
        Authorization: "Bearer " + (await AuthStorage.getFreshToken()),
        "Content-Type": "application/json",
      },
    });

    await AuthStorage.setSession(response.data.data);
    return response.data.data.accessToken;
  } catch (refreshError: any) {
    console.log("Refresh token failed", refreshError);

    if (refreshError?.response?.status === 401) {
      await handleUnauthorizedError();
    }
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};
